import { useMemo, useState } from "react";
import axios from "axios";
import { AlertTriangle, ExternalLink, Loader2, MapPin, Phone } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { trackListingCta } from "@/utils/ctaAnalytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_COPY = {
  available: {
    title: "Continue to the venue website?",
    message: "The site responded successfully. Continue when you’re ready to leave Wellness Collective AI.",
  },
  checking: {
    title: "Checking the venue website",
    message: "We’re checking the venue’s site before sending you away from the directory.",
  },
  idle: {
    title: "We could not verify this website",
    message: "We could not confirm the site’s status. You can try it with caution or stay in the directory.",
  },
  unavailable: {
    title: "This website is temporarily unavailable",
    message: "The venue’s site did not respond successfully. Stay here and use a verified contact option instead.",
  },
  unknown: {
    title: "We could not verify this website",
    message: "We could not confirm the site’s status. You can try it with caution or stay in the directory.",
  },
};

function publicWebsiteUrl(value) {
  try {
    const parsed = new URL(value);
    return ["http:", "https:"].includes(parsed.protocol) ? parsed.href : "";
  } catch {
    return "";
  }
}

function WebsiteStatusIcon({ status }) {
  if (status === "checking") return <Loader2 className="h-5 w-5 animate-spin" />;
  if (status === "available") return <ExternalLink className="h-5 w-5" />;
  return <AlertTriangle className="h-5 w-5" />;
}

function WebsiteFallbacks({ listing, mapsUrl, placement, testId }) {
  return <div className="grid gap-2 sm:grid-cols-2" data-testid={`${testId}-fallbacks`}>
    {listing.phone && <a href={`tel:${listing.phone}`} onClick={() => trackListingCta(listing.id, "phone", placement)} className="inline-flex min-h-11 items-center justify-center gap-2 border border-navy px-4 py-2 font-body text-sm font-bold text-navy" data-testid={`${testId}-fallback-phone`}><Phone className="h-4 w-4" />Call venue</a>}
    <a href={mapsUrl} target="_blank" rel="noopener noreferrer" onClick={() => trackListingCta(listing.id, "directions", placement)} className="inline-flex min-h-11 items-center justify-center gap-2 bg-navy px-4 py-2 font-body text-sm font-bold text-white" data-testid={`${testId}-fallback-directions`}><MapPin className="h-4 w-4" />Directions</a>
  </div>;
}

function WebsiteDialogActions({ recordReferral, safeUrl, status, testId }) {
  const canContinue = ["available", "unknown"].includes(status) && safeUrl;
  const cancelLabel = status === "unavailable" ? "Close" : "Stay here";
  const continueLabel = status === "available" ? "Continue to website" : "Try website anyway";
  return <AlertDialogFooter>
    <AlertDialogCancel data-testid={`${testId}-cancel`}>{cancelLabel}</AlertDialogCancel>
    {canContinue && <AlertDialogAction asChild><a href={safeUrl} target="_blank" rel="noopener noreferrer" onClick={recordReferral} data-testid={`${testId}-continue`}>{continueLabel}</a></AlertDialogAction>}
  </AlertDialogFooter>;
}

function useWebsiteStatus(listing, placement, safeUrl) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("idle");
  const check = async (event) => {
    event.stopPropagation();
    trackListingCta(listing.id, "website", placement);
    setOpen(true);
    if (status !== "idle") return;
    if (!safeUrl) { setStatus("unavailable"); return; }
    setStatus("checking");
    try {
      const response = await axios.get(`${API}/listings/${listing.id}/website-status`);
      setStatus(response.data?.status || "unknown");
    } catch {
      setStatus("unknown");
    }
  };
  return { check, open, setOpen, status };
}

export function SafeWebsiteAction({ listing, placement, className, testId, children, mapsUrl: suppliedMapsUrl }) {
  const safeUrl = useMemo(() => publicWebsiteUrl(listing.website), [listing.website]);
  const mapsUrl = suppliedMapsUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${listing.name} ${listing.address || listing.zone || listing.city || ""}`)}`;
  const { check, open, setOpen, status } = useWebsiteStatus(listing, placement, safeUrl);
  const copy = STATUS_COPY[status] || STATUS_COPY.unknown;
  const recordReferral = () => {
    axios.post(`${API}/referral-click`, { listingId: listing.id, listingName: listing.name, url: safeUrl }).catch(() => {});
  };

  return <>
    <button type="button" onClick={check} className={className} data-testid={testId}>{children}</button>
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent className="max-w-md border-t-4 border-t-brand-red" data-testid={`${testId}-dialog`}>
        <AlertDialogHeader>
          <div className={`mb-2 inline-flex h-10 w-10 items-center justify-center ${status === "available" ? "bg-green-100 text-green-700" : "bg-red-50 text-brand-red"}`} aria-hidden="true"><WebsiteStatusIcon status={status} /></div>
          <AlertDialogTitle className="font-heading text-2xl text-navy" data-testid={`${testId}-status-title`}>{copy.title}</AlertDialogTitle>
          <AlertDialogDescription className="font-body text-sm leading-relaxed text-gray-600" data-testid={`${testId}-status-message`}>{copy.message}</AlertDialogDescription>
        </AlertDialogHeader>
        {status === "unavailable" && <WebsiteFallbacks listing={listing} mapsUrl={mapsUrl} placement={placement} testId={testId} />}
        <WebsiteDialogActions recordReferral={recordReferral} safeUrl={safeUrl} status={status} testId={testId} />
      </AlertDialogContent>
    </AlertDialog>
  </>;
}